import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { models } from '../data';
import { NeonText } from '../components/effects';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { RetroGrid } from '../components/effects';
import { ModelViewer } from '../components/common';
import { useLanguage } from '../i18n';
import styles from './PlaygroundPage.module.css';

interface ViewerInfo {
  id: string;
  title: string;
  description: string;
  features: string[];
  featuresEn?: string[];
  optimization: string[];
  optimizationEn?: string[];
  tech: string[];
}

const VIEWER_INFO: Record<string, ViewerInfo> = {
  model3d: {
    id: 'model3d',
    title: 'Visor 3D Interactivo',
    description:
      'Visualizador de modelos 3D con soporte para múltiples formatos y animaciones en tiempo real. Utiliza iluminación HDR basada en imágenes (IBL) para un renderizado realista.',
    features: [
      'Soporte para GLB, GLTF, FBX y OBJ',
      'Animaciones embebidas con blending',
      'Iluminación HDR configurable (IBL)',
      'Sombras dinámicas en tiempo real',
      'Controles orbitales con damping',
    ],
    featuresEn: [
      'Support for GLB, GLTF, FBX and OBJ',
      'Embedded animations with blending',
      'Configurable HDR lighting (IBL)',
      'Real-time dynamic shadows',
      'Orbital controls with damping',
    ],
    optimization: [],
    tech: ['Three.js', 'React Three Fiber', 'WebGL 2.0', 'ACES Tonemapping'],
  },
  renderCreator: {
    id: 'renderCreator',
    title: 'Render Creator - Configurador de Interiores',
    description:
      'Herramienta profesional de configuración de espacios en tiempo real desarrollada en Unity URP. Permite visualizar diferentes acabados, materiales y configuraciones de baños con calidad fotorrealista manteniendo un rendimiento óptimo para web.',
    features: [
      'Cambio de materiales PBR en tiempo real',
      'Sistema de cámara orbital con FOV ajustable',
      'Múltiples configuraciones de espacio',
      'Captura de renders de alta calidad',
      'Interfaz UI intuitiva para selección de acabados',
    ],
    featuresEn: [
      'Real-time PBR material switching',
      'Orbital camera system with adjustable FOV',
      'Multiple space configurations',
      'High-quality render capture',
      'Intuitive UI for finish selection',
    ],
    optimization: [
      'Reducción de poligonaje: modelos optimizados de +500K a ~50K polys',
      'Compresión de texturas: ASTC/ETC2 con atlas de materiales',
      'Lightmaps bakeados: iluminación GI precalculada de alta calidad',
      'LOD System: niveles de detalle progresivos',
      'Occlusion Culling: renderizado selectivo por visibilidad',
      'GPU Instancing para objetos repetidos',
      'Texture Streaming para carga progresiva',
    ],
    optimizationEn: [
      'Polygon reduction: models optimized from +500K to ~50K polys',
      'Texture compression: ASTC/ETC2 with material atlases',
      'Baked lightmaps: high-quality pre-calculated GI lighting',
      'LOD System: progressive detail levels',
      'Occlusion Culling: visibility-based selective rendering',
      'GPU Instancing for repeated objects',
      'Texture Streaming for progressive loading',
    ],
    tech: ['Unity 2022 LTS', 'Universal Render Pipeline (URP)', 'WebGL 2.0', 'Lightmapping', 'PBR Materials', 'Custom Shaders'],
  },
  houseNavigator: {
    id: 'houseNavigator',
    title: 'Navegador 3D de Vivienda',
    description:
      'Recorrido virtual interactivo por una vivienda completa desarrollado en Unity URP. Permite explorar el espacio en primera persona con controles de navegación intuitivos y calidad visual de nivel arquitectónico.',
    features: [
      'Navegación en primera persona fluida',
      'Sistema de colisiones preciso',
      'Transiciones suaves entre estancias',
      'Iluminación realista día/noche',
      'Interacción con elementos del entorno',
    ],
    featuresEn: [
      'Smooth first-person navigation',
      'Precise collision system',
      'Smooth transitions between rooms',
      'Realistic day/night lighting',
      'Interaction with environment elements',
    ],
    optimization: [
      'Escena optimizada: de +2M polys a ~200K polys',
      'Texturas comprimidas: reducción del 80% en memoria VRAM',
      'Lightmaps de alta resolución: GI bakeada con Enlighten',
      'Reflection Probes: reflejos precalculados por zonas',
      'Batching estático y dinámico para draw calls mínimos',
      'Level Streaming: carga por zonas bajo demanda',
      'Post-processing optimizado: Bloom, AO, Color Grading',
    ],
    optimizationEn: [
      'Optimized scene: from +2M polys to ~200K polys',
      'Compressed textures: 80% VRAM memory reduction',
      'High-resolution lightmaps: GI baked with Enlighten',
      'Reflection Probes: pre-calculated zone-based reflections',
      'Static and dynamic batching for minimal draw calls',
      'Level Streaming: on-demand zone loading',
      'Optimized post-processing: Bloom, AO, Color Grading',
    ],
    tech: ['Unity 2022 LTS', 'Universal Render Pipeline (URP)', 'WebGL 2.0', 'Baked GI', 'Reflection Probes', 'Post-Processing Stack'],
  },
};

interface InfoModalProps {
  info: ViewerInfo;
  onClose: () => void;
}

function InfoModal({ info, onClose }: InfoModalProps) {
  const { t, language } = useLanguage();
  const hasOptimization = info.optimization && info.optimization.length > 0;

  const features = language === 'en' && info.featuresEn ? info.featuresEn : info.features;
  const optimization = language === 'en' && info.optimizationEn ? info.optimizationEn : info.optimization;

  return (
    <motion.div
      className={styles.modalOverlay}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      onClick={onClose}
    >
      {/* Animated background particles */}
      <div className={styles.modalParticles}>
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className={styles.particle}
            initial={{ opacity: 0, scale: 0 }}
            animate={{
              opacity: [0, 0.5, 0],
              scale: [0, 1, 0],
              x: Math.random() * 400 - 200,
              y: Math.random() * 400 - 200,
            }}
            transition={{
              duration: 2,
              delay: i * 0.1,
              repeat: Infinity,
              repeatDelay: Math.random() * 2,
            }}
          />
        ))}
      </div>

      <motion.div
        className={styles.modal}
        initial={{ scale: 0, opacity: 0, rotateX: -15 }}
        animate={{ scale: 1, opacity: 1, rotateX: 0 }}
        exit={{ scale: 0.8, opacity: 0, rotateX: 15 }}
        transition={{
          type: 'spring',
          damping: 20,
          stiffness: 300,
          mass: 0.8,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Animated border glow */}
        <motion.div
          className={styles.modalBorderGlow}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        />
        <div className={styles.modalGlow} />
        <motion.div
          className={styles.modalScanline}
          initial={{ top: 0 }}
          animate={{ top: '100%' }}
          transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
        />

        <motion.button
          className={styles.modalClose}
          onClick={onClose}
          whileHover={{ scale: 1.1, rotate: 90 }}
          whileTap={{ scale: 0.9 }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </motion.button>

        <div className={styles.modalContent}>
          <motion.div
            className={styles.modalHeader}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <motion.div
              className={styles.modalIcon}
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: 'spring', delay: 0.2 }}
            >
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M12 2L2 7l10 5 10-5-10-5z" />
                <path d="M2 17l10 5 10-5" />
                <path d="M2 12l10 5 10-5" />
              </svg>
            </motion.div>
            <h2 className={styles.modalTitle}>{info.title}</h2>
          </motion.div>

          <motion.p
            className={styles.modalDescription}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.15 }}
          >
            {info.description}
          </motion.p>

          <div className={styles.modalSections}>
            <motion.div
              className={styles.modalSection}
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <h3 className={styles.modalSectionTitle}>
                <span className={styles.sectionIcon}>◆</span>
                {t.playgroundPage.modal.features}
              </h3>
              <ul className={styles.modalFeatures}>
                {features.map((feature, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.25 + index * 0.05 }}
                  >
                    <span className={styles.featureIcon}>▸</span>
                    {feature}
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            {hasOptimization && (
              <motion.div
                className={styles.modalSection}
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
              >
                <h3 className={styles.modalSectionTitle}>
                  <span className={styles.sectionIcon}>⚡</span>
                  {t.playgroundPage.modal.optimization}
                </h3>
                <ul className={styles.modalFeatures}>
                  {optimization.map((opt, index) => (
                    <motion.li
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.35 + index * 0.04 }}
                      className={styles.optimizationItem}
                    >
                      <span className={styles.featureIcon}>●</span>
                      {opt}
                    </motion.li>
                  ))}
                </ul>
              </motion.div>
            )}
          </div>

          <motion.div
            className={styles.modalSection}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <h3 className={styles.modalSectionTitle}>
              <span className={styles.sectionIcon}>◇</span>
              {t.playgroundPage.modal.techStack}
            </h3>
            <div className={styles.modalTech}>
              {info.tech.map((tech, index) => (
                <motion.span
                  key={index}
                  className={styles.techTag}
                  initial={{ opacity: 0, scale: 0.5, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{
                    delay: 0.45 + index * 0.05,
                    type: 'spring',
                    stiffness: 200,
                  }}
                  whileHover={{ scale: 1.05, y: -2 }}
                >
                  {tech}
                </motion.span>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  );
}

export function PlaygroundPage() {
  const { t } = useLanguage();

  const RENDER_CREATOR_VIEWERS = [
    {
      id: 'bath1',
      name: t.playgroundPage.bathrooms.bath1,
      url: '/UnityWebGl/HausFIg_RenderCreator_ALTEA/index.html',
    },
    {
      id: 'bath2',
      name: t.playgroundPage.bathrooms.bath2,
      url: '/UnityWebGl/HausFIg_RenderCreator_BAIONA/index.html',
    },
    {
      id: 'bath3',
      name: t.playgroundPage.bathrooms.bath3,
      url: '/UnityWebGl/HausFIg_RenderCreator_JAVEA/index.html',
    },
    {
      id: 'bath4',
      name: t.playgroundPage.bathrooms.bath4,
      url: '/UnityWebGl/HausFIg_RenderCreator_LUARCA/index.html',
    },
  ];

  const [activeViewer, setActiveViewer] = useState(RENDER_CREATOR_VIEWERS[0]);
  const [activeModal, setActiveModal] = useState<ViewerInfo | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (activeModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [activeModal]);

  return (
    <>
      <RetroGrid />
      <Header />

      <main className={styles.main}>
        <div className={styles.container}>
          {/* Back Link */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
          >
            <Link to="/" className={styles.backLink}>
              {t.playgroundPage.backToHome}
            </Link>
          </motion.div>

          {/* Header */}
          <motion.header
            className={styles.header}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <NeonText as="h1" color="cyan" effect="glow" className={styles.title} animate={false}>
              {t.playgroundPage.title}
            </NeonText>
            <p className={styles.subtitle}>{t.playgroundPage.subtitle}</p>
          </motion.header>

          {/* Render Creator */}
          <motion.section
            className={styles.viewerCard}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className={styles.viewerInfo}>
              <div className={styles.viewerInfoText}>
                <h3 className={styles.viewerTitle}>{t.playgroundPage.viewers.renderCreator.title}</h3>
                <p className={styles.viewerDescription}>
                  {t.playgroundPage.viewers.renderCreator.description}
                </p>
              </div>
              <button className={styles.infoButton} onClick={() => setActiveModal(VIEWER_INFO.renderCreator)}>
                <span className={styles.infoButtonIcon}>i</span>
                {t.playgroundPage.moreInfo}
              </button>
            </div>
            <div className={styles.viewerContent}>
              <div className={styles.viewerTabs}>
                {RENDER_CREATOR_VIEWERS.map((viewer) => (
                  <button
                    key={viewer.id}
                    className={`${styles.viewerTab} ${activeViewer.id === viewer.id ? styles.viewerTabActive : ''}`}
                    onClick={() => setActiveViewer(viewer)}
                  >
                    {viewer.name}
                  </button>
                ))}
              </div>
              <div className={styles.iframeWrapper}>
                <iframe
                  key={activeViewer.id}
                  src={activeViewer.url}
                  title={`Render Creator - ${activeViewer.name}`}
                  className={styles.iframe}
                  allow="fullscreen"
                  scrolling="no"
                />
              </div>
            </div>
          </motion.section>

          {/* Navegador 3D */}
          <motion.section
            className={styles.viewerCard}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <div className={styles.viewerInfo}>
              <div className={styles.viewerInfoText}>
                <h3 className={styles.viewerTitle}>{t.playgroundPage.viewers.houseNavigator.title}</h3>
                <p className={styles.viewerDescription}>
                  {t.playgroundPage.viewers.houseNavigator.description}
                </p>
              </div>
              <button className={styles.infoButton} onClick={() => setActiveModal(VIEWER_INFO.houseNavigator)}>
                <span className={styles.infoButtonIcon}>i</span>
                {t.playgroundPage.moreInfo}
              </button>
            </div>
            <div className={styles.viewerContent}>
              <div className={styles.iframeWrapper}>
                <iframe
                  src="/UnityWebGl/webgl3dviewer/index.html"
                  title={t.playgroundPage.viewers.houseNavigator.title}
                  className={styles.iframe}
                  allow="fullscreen"
                  scrolling="no"
                />
              </div>
            </div>
          </motion.section>

          {/* Visor 3D */}
          <motion.section
            className={styles.viewerCard}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className={styles.viewerInfo}>
              <div className={styles.viewerInfoText}>
                <h3 className={styles.viewerTitle}>{t.playgroundPage.viewers.model3d.title}</h3>
                <p className={styles.viewerDescription}>
                  {t.playgroundPage.viewers.model3d.description}
                </p>
              </div>
              <button className={styles.infoButton} onClick={() => setActiveModal(VIEWER_INFO.model3d)}>
                <span className={styles.infoButtonIcon}>i</span>
                {t.playgroundPage.moreInfo}
              </button>
            </div>
            <div className={styles.viewerContent}>
              <ModelViewer
                models={models}
                variant="full"
                autoRotate={false}
                showControls={true}
                showInfo={true}
                showToolbar={true}
                lazy={true}
              />
            </div>
          </motion.section>

          {/* Próximamente */}
          <motion.section
            className={styles.viewerCard}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <div className={styles.viewerInfo}>
              <div className={styles.viewerInfoText}>
                <h3 className={styles.viewerTitle}>{t.playgroundPage.viewers.comingSoon.title}</h3>
                <p className={styles.viewerDescription}>
                  {t.playgroundPage.viewers.comingSoon.description}
                </p>
              </div>
              <button className={styles.infoButton} disabled>
                <span className={styles.infoButtonIcon}>i</span>
                {t.playgroundPage.inDevelopment}
              </button>
            </div>
            <div className={styles.viewerContent}>
              <div className={styles.comingSoon}>
                <div className={styles.comingSoonIcon}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                </div>
                <p className={styles.comingSoonText}>{t.playgroundPage.nextExperiment}</p>
              </div>
            </div>
          </motion.section>
        </div>
      </main>

      <Footer />

      {/* Modal */}
      <AnimatePresence>{activeModal && <InfoModal info={activeModal} onClose={() => setActiveModal(null)} />}</AnimatePresence>
    </>
  );
}
